import { marked } from "marked";
import sanitizeHtml from "sanitize-html";

/**
 * Render project instructions (labeling / review instructions) to safe HTML.
 *
 * Instructions may be written in Markdown, in HTML, or in a mix: Markdown is
 * converted first (inline HTML passes through untouched, single line breaks
 * become <br> so plain-text instructions keep their layout) and the result is
 * then sanitised. Every surface that shows instructions (the Settings preview,
 * the editor's Instructions modal, the Data Manager modals) goes through this
 * one function so what admins preview is what annotators see.
 */
const ALLOWED_TAGS = [
  ...sanitizeHtml.defaults.allowedTags,
  "img",
  "del",
  "ins",
  "details",
  "summary",
  "iframe",
  "video",
  "audio",
  "source",
];

const ALLOWED_ATTRIBUTES: sanitizeHtml.IOptions["allowedAttributes"] = {
  ...sanitizeHtml.defaults.allowedAttributes,
  a: ["href", "name", "target", "rel", "title"],
  img: ["src", "alt", "title", "width", "height"],
  td: ["colspan", "rowspan", "align"],
  th: ["colspan", "rowspan", "align"],
  iframe: ["src", "width", "height", "title", "allowfullscreen", "frameborder"],
  video: ["src", "controls", "width", "height", "poster", "preload", "muted", "loop"],
  audio: ["src", "controls", "preload", "loop"],
  source: ["src", "type"],
  "*": ["align"],
};

const OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ALLOWED_TAGS,
  allowedAttributes: ALLOWED_ATTRIBUTES,
  allowedSchemes: ["http", "https", "mailto"],
  allowedSchemesByTag: {
    img: ["http", "https", "data"],
    iframe: ["http", "https"],
    video: ["http", "https"],
    audio: ["http", "https"],
    source: ["http", "https"],
  },
  // Files added through "Add file" live on this origin (/data/upload/...).
  // Other frames are limited to the players the app's frame policy
  // (CSP_EMBED_HOSTS in core/settings/base.py) lets a page embed; keep the
  // two lists the same.
  allowIframeRelativeUrls: true,
  allowedIframeHostnames: [
    "www.youtube.com",
    "www.youtube-nocookie.com",
    "player.vimeo.com",
    "drive.google.com",
    "docs.google.com",
  ],
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { target: "_blank", rel: "noopener noreferrer" }),
  },
};

export function instructionsToHtml(text: string | null | undefined): string {
  const source = String(text ?? "").trim();
  if (!source) return "";
  const html = marked.parse(source, { gfm: true, breaks: true, headerIds: false, mangle: false }) as string;
  return sanitizeHtml(html, OPTIONS);
}
